<template>
<!-- eslint-disable max-len -->
<span>
  <top-bar :show-player="showName" :role="selectedRole">
    <div
      v-if="showButtons && !singleRole"
      style="width: 100%; text-align: center;"
    >SELECT YOUR ROLE</div>
  </top-bar>

  <transition enter-active-class="animated slideInLeft" leave-active-class="animated fadeOut">
    <player v-if="showPlayer" class="role-avatar" :class="{ native }" :variantOverride="selectedRole.playerVariant"></player>
  </transition>

  <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
    <div v-if="showRoleDescription"
      class="role-view"
      :class="{ native }"
    >
      <!--
        Never use v-html with end-user generated content, because it's a vector for XSS attacks
        This is what the following line used to be:
        <h1>{{ selectedRole.title }}</h1>
      -->
      <h1 v-html="selectedRole.title"></h1>

      <template v-if="native">
        <div class="role-wrapper">
          <template v-if="group.players.length > 1">
            <h2 v-if="selectedRoleUser">
              Role is played by:
              {{ selectedRoleUser.firstName || selectedRoleUser.email }}
            </h2>
            <h2 v-else>Role is available</h2>
          </template>

          <!--
            Never use v-html with end-user generated content, because it's a vector for XSS attacks
            This is what the following line used to be:
            <paragraphs class="role-description" :text="selectedRole.description"></paragraphs>
          -->
          <p class="role-description" v-html="selectedRole.description"></p>

          <img
            v-show="selectedRole.image"
            :src="resolveAssetPath(selectedRole.image, 'images')"
          />
        </div>
      </template>
      <template v-else>
        <template v-if="group.players.length > 1">
          <h2 v-if="selectedRoleUser">
            Role is played by:
            {{ selectedRoleUser.firstName || selectedRoleUser.email }}
          </h2>
          <h2 v-else>Role is available</h2>
        </template>

        <!--
          Never use v-html with end-user generated content, because it's a vector for XSS attacks,
          but this is okay here since selectedRole.description is only used/set internally.
          AFAIK, this is the simplest way to support hyperlinks in dialogue, and it also enables any html formatting.
          This is what the following line used to be:
          <paragraphs class="role-description" :text="selectedRole.description"></paragraphs>
        -->
        <p class="role-description" v-html="selectedRole.description"></p>
      </template>
    </div>
  </transition>

  <template v-if="selectableRoles.length > 1">
    <transition enter-active-class="animated fadeIn">
      <button v-if="showButtons" id="left-select" class="btn select" @click.prevent.stop="left" v-focus autofocus>
        <img :src="resolveAssetPath('ArrowL_Tall.png', 'ui/customer')">
      </button>
    </transition>
    <transition enter-active-class="animated fadeIn">
      <button v-if="showButtons" id="right-select" class="btn select" @click.prevent.stop="right">
        <img :src="resolveAssetPath('ArrowL_Tall.png', 'ui/customer')">
      </button>
    </transition>
  </template>

  <bottom-bar :next="selectedRoleUser || !showButton ? undefined : navigateAndSaveSelection" :no-focus="!singleRole">
    <span slot="button">
      <template v-if="singleRole">{{(game && game.nextButtonDefaultText) ? game.nextButtonDefaultText : 'Next'}}</template>
      <template v-else>Select</template>
    </span>
  </bottom-bar>
</span>
</template>

<script>
import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';

import Paragraphs from '../../ui/display/Paragraphs';
import TopBar from '../ui/TopBar';
import Player from '../ui/Player';
import BottomBar from '../ui/BottomBar';

export default {
  components: {
    Paragraphs,
    TopBar,
    Player,
    BottomBar
  },
  data() {
    return {
      showPlayer: false,
      showName: false,
      showRoleDescription: false,
      showButtons: false,
      showButton: false,
      selectedRoleIndex: 0,
      // use to freeze the ui, even though state updates
      finalSelectedRole: undefined,
      finalSelectedRoleUser: undefined,
      errorCount: 0,
      maxErrors: 3,
      updating: false,
      intervalID: null,
      failCount: 0,
    };
  },
  computed: {
    ...mapGetters([
      'group',
      'otherRoles',
      'otherPlayers',
      'roles',
      'updateGroup',
      'users',
      'singleRole',
      'game',
      'challenge',
      'starfighter',
      'native'
    ]),
    requiredRoles() {
      return this.game.roles.filter(role => role.required);
    },
    unassignedPlayerCount() {
      return this.group.players.length
        - (this.otherPlayers.filter(player => (player.role && player.role._id)).length);
    },
    selectableRoles() {
      // first check if there are more unassigned players than unassigned required roles
      const unassignedRequiredRoles = this.otherRoles.filter(
        role => (role.player == null) && this.requiredRoles.some(required => `${required._id}` === `${role._id}`));
      if (unassignedRequiredRoles.length > 0
        && this.unassignedPlayerCount <= unassignedRequiredRoles.length) {
        // if not, filter the role list to show what the other players
        // have chosen and the unassigned required roles.
        // this makes sure that the required roles are always chosen.
        const sr = this.otherRoles.filter(
          role => role.player
            || unassignedRequiredRoles.some(required => `${required._id}` === `${role._id}`));

        // if the new role list is shorter than the role index you're on, reset the index.
        // (prevents index out of bounds)
        if (sr.length <= this.selectedRoleIndex) {
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          this.selectedRoleIndex = 0;
        }

        return sr;
      }
      // if there are more unassigned players than unassigned required roles,
      // give all roles as options.
      // this way the first players to choose aren't limited to only the required roles.
      // only the later players are potentially limited.
      return this.otherRoles;
    },
    selectedGroupRole() {
      return this.selectableRoles[this.selectedRoleIndex] || {};
    },
    selectedRole() {
      const selectedRole =
        this.finalSelectedRole || this.roles[this.selectedGroupRole._id];
      return selectedRole || {};
    },
    selectedRoleUser() {
      if (this.finalSelectedRoleUser) return this.finalSelectedRoleUser;
      const { player, playedBy, ai } = this.selectedGroupRole;
      return ai ? { firstName: 'AI Player' } : this.users[player || playedBy];
    }
  },
  methods: {
    ...mapActions([
      'playVoice',
      'stopSounds',
      'getMyGroup',
      'setTempSelectedRole',
      'selectRole',
      'pollGroup'
    ]),
    async left() {
      const newIndex = this.selectedRoleIndex - 1;
      this.selectedRoleIndex = newIndex < 0 ? this.selectableRoles.length - 1 : newIndex;
      await Vue.nextTick();
      const { selectedRole } = this;
      this.setTempSelectedRole({ selectedRole });
    },
    async right() {
      this.selectedRoleIndex = (this.selectedRoleIndex + 1) % this.selectableRoles.length;
      await Vue.nextTick();
      const { selectedRole } = this;
      this.setTempSelectedRole({ selectedRole });
    },
    async navigateAndSaveSelection() {
      if (this.updating) return;
      if (this.finalSelectedRole) return;
      const { player, playedBy, ai } = this.selectedGroupRole;
      if (player || playedBy || ai) return;

      this.finalSelectedRole = this.selectedRole;
      this.finalSelectedRoleUser = this.selectedRoleUser;

      try {
        await this.selectRole({ role: this.selectedRole });
        await this.hide();
        this.$router.nextGameState();
      } catch (err) {
        if (await this.$router.handleGameError(err)) return;
        this.errorCount += 1;
        if (this.errorCount >= this.maxErrors) return this.$router.showGameErrorCritical();
        // TODO: flash message ?
        this.finalSelectedRole = undefined;
        this.finalSelectedRoleUser = undefined;
      }
    },
    async show() {
      if (this.challenge.skipRoleDisplay) {
        await this.navigateAndSaveSelection();
      } else {
        await this.$nextTick();
        this.stopSounds();
        this.showPlayer = true;
        await new Promise(resolve => setTimeout(resolve, 1250));
        this.showName = true;
        this.showRoleDescription = true;
        if (this.selectedRole.titleVoiceLine) {
          this.playVoice({ line: this.selectedRole.titleVoiceLine });
        }
        if (this.selectedRole.descriptionVoiceLine) {
          await this.$nextTick();
          this.playVoice({ line: this.selectedRole.descriptionVoiceLine, queue: !!(this.selectedRole.titleVoiceLine), });
        }
        await new Promise(resolve => setTimeout(resolve, 1250));
        this.showButtons = true;
        this.showButton = true;
      }
    },
    async hide() {
      this.showButtons = false;
      this.showButton = false;
      this.showRoleDescription = false;
      if (!(this.challenge.skipRoleDisplay)) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      this.showPlayer = false;
      if (!(this.challenge.skipRoleDisplay)) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    },
    async poll() {
      try {
        // TODO: should use the updating flag here when triggered by peerjs
        // this.updating = true;
        await this.pollGroup();
        this.failCount = 0;
        // this.updating = false;
      } catch (err) {
        // this.updating = false;
        this.failCount += 1;
        if (this.failCount > 3) {
          if (await this.$router.handleGameError(err)) return;
          this.$router.showGameErrorCritical();
        }
      }
    }
  },
  watch: {
    async updateGroup(newValue) {
      if (!newValue) return;

      try {
        this.updating = true;
        const { _id: groupId } = this.group;
        await this.getMyGroup({ groupId });
        this.updating = false;
      } catch (err) {
        if (await this.$router.handleGameError(err)) return;
        this.$router.showGameErrorCritical();
      }
    }
  },
  async created() {
    const { selectedRole } = this;
    this.setTempSelectedRole({ selectedRole });
  },
  async mounted() {
    await this.show();
    if (this.group.players.length > 1) {
      this.intervalID = window.setInterval(this.poll, 5000);
    }
  },
  beforeDestroy() {
    if (this.intervalID != null) {
      window.clearInterval(this.intervalID);
    }
  }
};
</script>

<style lang="scss" scoped>
.role-view {
  position: absolute;
  bottom: 70px;
  left: 125px;
  width: 1055px;
  height: auto;
  background-color: white;
}
.role-view.native {
  bottom: 150px;
  left: -1%;
  width: 102%;
  background-color: unset;
  z-index: -100;
}
.role-avatar.native {
  // single drop-shadow gets softer as it gets wider, so using double
  // drop-shadow to allow it to be wider and darker at the same time
  filter: drop-shadow(0 0 0.85rem rgba(0, 0, 0, 0.5)) drop-shadow(0 0 0.85rem rgba(0, 0, 0, 1));
}
.role-view h1 {
  background-color: black;
  color: white;
  font-size: 54px;
  text-align: center;
  padding: 5px;
  margin: 0;
}
.role-view.native h1 {
  border: 2px solid white;
  background-color: var(--neutralColor);
  color: white;
  font-size: 35px;
  text-align: center;
  vertical-align: center;
  padding: 28px 30% 28px 35%;
  margin: 0;
  margin-bottom: 24px;
}
.role-view h2 {
  color: black;
  text-align: center;
  padding: 15px 15px 0;
  font-size: 33px;
  font-weight: bold;
  margin: 0;
}
.role-view.native h2 {
  color: white;
  font-size: 30px;
  line-height: 35px;
  font-weight: normal;
  text-align: center;
  vertical-align: center;
  margin: 0 30% 18px 35%;
  padding: 0;
}
.role-view .role-description {
  padding: 20px 25px;
  overflow: auto;
  color: black;
  font-size: 36px;
  line-height: 40px;
  // font-weight: bold;
  text-align: left;
  overflow: auto;
  max-height: 276px;
}
.role-view.native .role-description {
  color: white;
  font-size: 30px;
  line-height: 1.2; // any lower than this breaks overflow: auto for some reason
  font-weight: normal;
  text-align: left;
  vertical-align: center;
  margin: 0 30% 0 35%;
  padding: 0;
  overflow: auto;
  max-height: 420px;
}
.role-view.native .role-wrapper {
  border: 2px solid white;
  background-color: var(--neutralColor);
  min-height: 250px;
  max-height: 510px;
  position: relative;
  padding: 28px;
}
.role-view.native .role-wrapper img {
  object-fit: contain;
  max-width: 350px;
  position: absolute;
  right: 125px;
  top: 0;
  padding: 15px;
  height: 100%;
}

.select {
  position: absolute;
  width: 80px;
  height: 240px;
  top: 320px;
}

#left-select {
  left: 25px;
}
#left-select:focus {
  outline: none;
}
#right-select {
  right: 25px;

  img {
    transform: rotate(180deg);
  }
}
#right-select:focus {
  outline: none;
}
</style>



// WEBPACK FOOTER //
// src/components/game/pages/RoleSelect.vue